import { format } from 'date-fns';
import { Component, inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { environment } from '../../../../environments/environment';
import { UtilitiesService } from '../../../services/utilities.service';
import { GenericService } from '../../../services/generic.services';

import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzResultModule } from 'ng-zorro-antd/result';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageModule } from 'ng-zorro-antd/message';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { RouterLink } from '@angular/router';
import { ISendSupportComplaintsBookRequest } from '@/app/models/claim-book-form.model';
import { FsApiService } from '@/app/services/auth/fs-api.service';
import { catchError, of, tap } from 'rxjs';

@Component({
  selector: 'app-claim-book-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NzGridModule,
    NzIconModule,
    NzImageModule,
    NzSkeletonModule,
    NzDividerModule,
    NzButtonModule,
    NzFormModule,
    NzResultModule,
    NzSelectModule,
    NzInputModule,
    NzMessageModule,
    NzRadioModule,
    RouterLink,
  ],
  templateUrl: './claim-book-form.component.html',
  styleUrls: ['./claim-book-form.component.scss'],
})
export class ClaimBookFormComponent implements OnInit {
  private fsApi = inject(FsApiService);

  sendingEmail: boolean = false;
  loadMessage: boolean = false;
  validateForm!: UntypedFormGroup;

  urlIMG = environment.urlIMG;

  day: any = '';

  typeArray: Array<string> = [
    this.translateService.instant('FormBook.Claim'),
    this.translateService.instant('FormBook.Complaint'),
  ];
  typePrefix: Array<{ country: string; prefix: string }> =
    this.utilitiesService.getPrefixPhone();

  constructor(
    private readonly translateService: TranslateService,
    private fb: UntypedFormBuilder,
    private utilitiesService: UtilitiesService,
    private message: NzMessageService,
    public genericServices: GenericService
  ) {}

  ngOnInit(): void {
    this.day = this.genericServices.getCurrentDate();

    window.scrollTo(0, 0);
    this.validateForm = this.fb.group({
      name: ['', [Validators.required]],
      address: ['', [Validators.required]],
      dni: ['', [Validators.required]],
      phoneNumberPrefix: ['+51'],
      phoneNumber: ['', [Validators.required]],
      email: ['', [Validators.email, Validators.required]],
      emailConfirm: ['', [this.confirmValidator]],
      typeClaim: ['', [Validators.required]],
      place: ['Marcadores247'],
      reference: [''],
      detail: ['', [Validators.required]],
      order: ['', [Validators.required]],
    });
  }

  submitForm(): void {
    if (this.validateForm.valid) {
      this.sendingEmail = true;

      window.scrollTo(0, 0);

      const {
        address,
        detail,
        dni,
        email,

        name,
        order,
        phoneNumber,
        phoneNumberPrefix,
        place,
        reference,
        typeClaim,
      } = this.validateForm.value;

      const date = new Date();
      const formattedDate =
        date.getFullYear() +
        '-' +
        String(date.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(date.getDate()).padStart(2, '0');

      this.sendSupportContainsBook({
        address,
        date: formattedDate,
        details: detail,
        dni,
        email,
        name,
        order,
        phoneNumber,
        phoneNumberPrefix,
        place,
        reference,
        typeComplaint: typeClaim,
      });
    } else {
      this.message.create(
        'warning',
        this.translateService.instant('FormValid.Invalid')
      );
      Object.values(this.validateForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }

  validateConfirmEmail(): void {
    setTimeout(() =>
      this.validateForm.controls['emailConfirm'].updateValueAndValidity()
    );
  }

  confirmValidator = (
    control: UntypedFormControl
  ): { [s: string]: boolean } => {
    if (!control.value) {
      return { error: true, required: true };
    } else if (control.value !== this.validateForm.controls['email'].value) {
      return { confirm: true, error: true };
    }
    return {};
  };

  resetForm(): void {
    this.validateForm.controls['name'].setValue('');
    this.validateForm.controls['address'].setValue('');
    this.validateForm.controls['dni'].setValue('');
    this.validateForm.controls['phoneNumberPrefix'].setValue('+51');
    this.validateForm.controls['phoneNumber'].setValue('');
    this.validateForm.controls['email'].setValue('');
    this.validateForm.controls['emailConfirm'].setValue('');
    this.validateForm.controls['typeClaim'].setValue('');
    this.validateForm.controls['place'].setValue('Marcadores247');
    this.validateForm.controls['reference'].setValue('');
    this.validateForm.controls['detail'].setValue('');
    this.validateForm.controls['order'].setValue('');
    for (const key in this.validateForm.controls) {
      if (this.validateForm.controls.hasOwnProperty(key)) {
        this.validateForm.controls[key].markAsPristine();
        this.validateForm.controls[key].updateValueAndValidity();
      }
    }
  }

  sendSupportContainsBook(request: ISendSupportComplaintsBookRequest) {
    const id = this.message.loading(
      this.translateService.instant('SendingEmail'),
      { nzDuration: 0 }
    ).messageId;

    this.fsApi
      .post<ISendSupportComplaintsBookRequest>(`/support/complaints-book`, {
        ...request,
      })
      .pipe(
        tap(() => {
          this.sendingEmail = false;
        }),
        catchError((_) => {
          setTimeout(() => {
            this.sendingEmail = false;
            this.message.remove(id);
            this.message.create(
              'error',
              this.translateService.instant('FormValid.NotSendingEmail')
            );
          }, 1000);
          return of(null);
        })
      )
      .subscribe((data: any) => {
        if (data === 'Success') {
          setTimeout(() => {
            this.sendingEmail = false;
            this.loadMessage = true;
            this.resetForm();
            this.message.remove(id);
          }, 1000);
        }
      });
  }
}
